import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  // standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  Copy,
  FileText,
  FolderOpen,
  Info,
  LoaderCircle,
  MoreHorizontal,
  Plus,
  Search,
  ShieldAlert,
  Sparkles,
  TriangleAlert,
  Undo2,
  X,
} from 'lucide-react';
import type {
  DocumentFilingRule,
  FilingExpression,
  FilingPlan,
  FilingPlanPreview,
  FilingToken,
} from '../shared/desktop-api';
import { WorkspaceDialog } from './WorkspaceDialog';
import { TooltipButton } from './TooltipButton';
import './naming-window.css';

/** The parts of a job this window reads; the App's own types satisfy them. */
export type NamingField = {
  id: string;
  name: string;
  kind: string;
  semanticId?: string | null;
  description?: string | null;
};
export type NamingDocumentType = {
  id: string;
  name: string;
  status: string;
  fields: NamingField[];
};
export type NamingJob = { id: string; name: string; documentTypes: NamingDocumentType[] };

type LaneKey = number | 'name';
type Selection = { lane: LaneKey; index: number };

const SEPARATORS: Array<{ value: string; label: string; name: string }> = [
  { value: '_', label: '_', name: 'Underscore' },
  { value: '-', label: '-', name: 'Dash' },
  { value: ' ', label: 'Space', name: 'Space' },
  { value: '', label: 'None', name: 'Nothing' },
];
const DATE_FORMATS: Array<{ value: string; label: string; example: string }> = [
  { value: 'yyyy', label: 'Year', example: '2026' },
  { value: 'yyyy-MM', label: 'Year-month', example: '2026-09' },
  { value: 'yyyy-MM-dd', label: 'Full date', example: '2026-09-14' },
  { value: 'MM', label: 'Month', example: '09' },
  { value: 'dd', label: 'Day', example: '14' },
];

/**
 * Whether a field's value is safe to show in a folder or file name. Mirrors the
 * engine's own check (FilingPlanEvaluator.IsVisibleNameSafe, the same words and phrases),
 * which is what decides the warning a save carries.
 */
export function visibleFilingField(field: {
  name: string;
  semanticId?: string | null;
  description?: string | null;
}): boolean {
  const profile =
    `${field.name} ${field.semanticId ?? ''} ${field.description ?? ''}`.toLowerCase();
  const words = new Set(profile.split(/[^\p{L}\p{N}]+/u).filter(Boolean));
  if (['password', 'secret', 'ssn', 'tin', 'itin', 'ein'].some((term) => words.has(term)))
    return false;
  return ![
    'social security',
    'taxpayer id',
    'tax id',
    'employer identification',
    'bank account',
    'routing number',
    'credit card',
    'card number',
    'protected health',
    'medical record number',
    'patient id',
    'driver license',
    "driver's license",
    'drivers license',
  ].some((term) => profile.includes(term));
}

const nameableFields = (type: NamingDocumentType) =>
  type.fields.filter((field) => field.kind !== 'line-items' && field.kind !== 'document-type');

const cloneRule = (rule: DocumentFilingRule): DocumentFilingRule =>
  JSON.parse(JSON.stringify(rule)) as DocumentFilingRule;
const sameRule = (left?: DocumentFilingRule, right?: DocumentFilingRule) =>
  JSON.stringify(left ?? null) === JSON.stringify(right ?? null);

function fieldToken(field: NamingField, format?: string | null): FilingToken {
  return {
    kind: 'field',
    fieldId: field.id,
    fallback: `Unknown ${field.name}`,
    format: field.kind === 'date' ? (format ?? 'yyyy-MM-dd') : null,
  };
}

/** A part's name as the operator knows it: the field, "Document type", or their words. */
function partLabel(token: FilingToken, type: NamingDocumentType): string {
  if (token.kind === 'document-type') return 'Document type';
  if (token.kind === 'literal') return 'Your own words';
  const field = type.fields.find((item) => item.id === token.fieldId);
  return field?.name ?? 'Missing field';
}

function partValue(token: FilingToken, type: NamingDocumentType): string {
  if (token.kind === 'document-type') return type.name;
  if (token.kind === 'literal') return token.literal?.trim() || 'Your words';
  const format = DATE_FORMATS.find((item) => item.value === token.format);
  return format ? format.label : 'Its value';
}

/** One line that says how a type is named, for the list on the left (and Paige's File naming card). */
export function namingSummary(
  rule: DocumentFilingRule | undefined,
  type: NamingDocumentType,
): string {
  if (!rule) return 'No naming yet';
  const expression = (value: FilingExpression) =>
    value.parts
      .map((part) => (part.kind === 'literal' ? (part.literal ?? '') : partLabel(part, type)))
      .filter(Boolean)
      .join(value.separator || '');
  return [...rule.folderSegments.map(expression), `${expression(rule.preferredBaseName)}.pdf`].join(
    ' › ',
  );
}

type TreeRow = {
  key: string;
  depth: number;
  kind: 'folder' | 'file';
  text: string;
  fallback: boolean;
};
function exampleTree(examples: FilingPlanPreview['examples'], fallbacks: string[]): TreeRow[] {
  const rows: TreeRow[] = [];
  const seen = new Set<string>();
  const usesFallback = (text: string) =>
    fallbacks.some((fallback) => fallback && text.includes(fallback));
  for (const example of examples) {
    example.folders.forEach((folder, depth) => {
      const key = example.folders.slice(0, depth + 1).join('\u0000');
      if (seen.has(key)) return;
      seen.add(key);
      rows.push({ key, depth, kind: 'folder', text: folder, fallback: usesFallback(folder) });
    });
    const fileKey = `${example.folders.join('\u0000')}\u0001${example.documentId}`;
    rows.push({
      key: fileKey,
      depth: example.folders.length,
      kind: 'file',
      text: `${example.preferredBaseName}.pdf`,
      fallback: usesFallback(example.preferredBaseName),
    });
  }
  // Keep each folder's files under it: sort by the folder path, folders first.
  const order = (row: TreeRow) => row.key.replace('\u0001', '\u0000￿');
  return rows.sort((left, right) => order(left).localeCompare(order(right)));
}

/**
 * File naming: every document type on the left, the chosen type's folders and
 * file name as lanes of parts in the middle, and the names its real recent
 * documents would get on the right. Nothing here is saved; "Use this naming"
 * hands the whole plan back to Export as an unsaved Job setup change. In Paige
 * (`savesNow`) it saves the job's naming at once: `onUse` answers a promise, and
 * a refusal keeps the window open with Paige's reason in it.
 */
export function NamingWindow({
  job,
  plan,
  destinationLabel,
  onUse,
  onClose,
  savesNow = false,
}: {
  job: NamingJob;
  plan: FilingPlan;
  destinationLabel: string;
  onUse: (plan: FilingPlan) => void | Promise<void>;
  onClose: () => void;
  savesNow?: boolean;
}) {
  const api = window.capturePointDesktop;
  const types = useMemo(
    () => job.documentTypes.filter((type) => type.status !== 'archived'),
    [job.documentTypes],
  );
  const [starter, setStarter] = useState<FilingPlan | null>(null);
  const [rules, setRules] = useState<DocumentFilingRule[]>(() => plan.rules.map(cloneRule));
  const [typeId, setTypeId] = useState(() => types[0]?.id ?? '');
  const [query, setQuery] = useState('');
  const [selection, setSelection] = useState<Selection | null>(null);
  const [adding, setAdding] = useState<LaneKey | null>(null);
  const [swapping, setSwapping] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [copyPicks, setCopyPicks] = useState<Set<string>>(new Set());
  const [notice, setNotice] = useState('');
  const [proposal, setProposal] = useState<{
    kind: 'suggested' | 'starter';
    plan: FilingPlan;
  } | null>(null);
  const [proposing, setProposing] = useState<'suggested' | 'starter' | null>(null);
  const [preview, setPreview] = useState<FilingPlanPreview | null>(null);
  const [previewState, setPreviewState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [previewError, setPreviewError] = useState('');
  const [previewRetry, setPreviewRetry] = useState(0);
  const [askDiscard, setAskDiscard] = useState(false);
  const [askClose, setAskClose] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  // Every type needs a rule before its naming can be drawn; the starter plan
  // is also "the suggestion" a changed type can go back to.
  useEffect(() => {
    if (!api) return;
    void api
      .getStarterFilingPlan(job.id)
      .then((value) => {
        if (!live.current) return;
        setStarter(value);
        setRules((current) => {
          const missing = value.rules.filter(
            (rule) => !current.some((item) => item.documentTypeId === rule.documentTypeId),
          );
          return missing.length ? [...current, ...missing.map(cloneRule)] : current;
        });
      })
      .catch(() => undefined);
  }, [api, job.id]);

  const draftPlan = useMemo<FilingPlan>(() => ({ ...plan, rules }), [plan, rules]);
  // A type the saved plan never named receives the suggestion when the window
  // opens; that alone is not a change the operator made.
  const dirty =
    rules.some((item) => {
      const saved = plan.rules.find(
        (candidate) => candidate.documentTypeId === item.documentTypeId,
      );
      const suggested = starter?.rules.find(
        (candidate) => candidate.documentTypeId === item.documentTypeId,
      );
      return saved ? !sameRule(item, saved) : !sameRule(item, suggested);
    }) ||
    plan.rules.some((saved) => !rules.some((item) => item.documentTypeId === saved.documentTypeId));

  useEffect(() => {
    if (!api || !typeId) return;
    let current = true;
    setPreviewState('loading');
    setPreviewError('');
    const timer = window.setTimeout(() => {
      void api
        .previewFilingPlan(job.id, draftPlan, { documentTypeId: typeId, limit: 12 })
        .then((value) => {
          if (current) {
            setPreview(value);
            setPreviewState('ready');
          }
        })
        .catch((reason) => {
          if (!current) return;
          setPreviewState('error');
          setPreviewError(
            reason instanceof Error ? reason.message : 'The examples could not be built.',
          );
        });
    }, 250);
    return () => {
      current = false;
      window.clearTimeout(timer);
    };
  }, [api, draftPlan, job.id, previewRetry, typeId]);

  const type = types.find((item) => item.id === typeId) ?? types[0];
  const rule = rules.find((item) => item.documentTypeId === type?.id);
  const starterRule = starter?.rules.find((item) => item.documentTypeId === type?.id);
  const lanes: LaneKey[] = rule ? [...rule.folderSegments.map((_, index) => index), 'name'] : [];
  const expressionOf = (value: DocumentFilingRule, lane: LaneKey) =>
    lane === 'name' ? value.preferredBaseName : value.folderSegments[lane];
  const selectedExpression = rule && selection ? expressionOf(rule, selection.lane) : undefined;
  const selectedPart = selectedExpression?.parts[selection?.index ?? -1];
  const selectedField =
    selectedPart?.kind === 'field'
      ? type?.fields.find((field) => field.id === selectedPart.fieldId)
      : undefined;

  const editRule = (
    change: (draft: DocumentFilingRule) => void,
    next?: Partial<{ selection: Selection | null }>,
  ) => {
    if (!type) return;
    setRules((current) =>
      current.map((item) => {
        if (item.documentTypeId !== type.id) return item;
        const draft = cloneRule(item);
        change(draft);
        return draft;
      }),
    );
    setNotice('');
    if (next && 'selection' in next) setSelection(next.selection ?? null);
  };
  const setPart = (lane: LaneKey, index: number, token: FilingToken) =>
    editRule((draft) => {
      expressionOf(draft, lane).parts[index] = token;
    });

  const partChoices = (onPick: (token: FilingToken) => void) =>
    type
      ? [
          {
            key: 'type',
            label: 'Document type',
            hint: type.name,
            private: false,
            pick: () => onPick({ kind: 'document-type' }),
          },
          ...nameableFields(type).map((field) => ({
            key: field.id,
            label: field.name,
            hint: field.kind === 'date' ? 'a date' : '',
            private: !visibleFilingField(field),
            pick: () => onPick(fieldToken(field)),
          })),
          {
            key: 'words',
            label: 'Your own words…',
            hint: '',
            private: false,
            pick: () => onPick({ kind: 'literal', literal: 'Documents' }),
          },
        ]
      : [];

  const examples =
    preview?.examples.filter(
      (example) =>
        example.documentType.localeCompare(type?.name ?? '', undefined, {
          sensitivity: 'accent',
        }) === 0,
    ) ?? [];
  const fallbacks = rule
    ? [...rule.folderSegments, rule.preferredBaseName].flatMap((expression) =>
        expression.parts.filter((part) => part.kind === 'field').map((part) => part.fallback ?? ''),
      )
    : [];
  const tree = exampleTree(examples.slice(0, 5), fallbacks);
  const fallbackCount = examples.filter((example) =>
    fallbacks.some(
      (fallback) =>
        fallback &&
        [...example.folders, example.preferredBaseName].some((text) => text.includes(fallback)),
    ),
  ).length;
  const privateFields =
    rule && type
      ? [
          ...new Set(
            [...rule.folderSegments, rule.preferredBaseName].flatMap((expression) =>
              expression.parts
                .filter((part) => part.kind === 'field')
                .map((part) => type.fields.find((field) => field.id === part.fieldId))
                .filter(
                  (field): field is NamingField => Boolean(field) && !visibleFilingField(field!),
                )
                .map((field) => field.name),
            ),
          ),
        ]
      : [];
  const errors =
    preview?.diagnostics.filter(
      (item) => item.severity !== 'warning' && item.severity !== 'info',
    ) ?? [];
  // Only a naming the engine has just checked can be used: a preview still building, or one that failed
  // (whose diagnostics are an earlier naming's), never vouches for this one.
  const canUse = previewState === 'ready' && errors.length === 0;
  const firstExample = examples[0];

  const proposeFrom = async (kind: 'suggested' | 'starter') => {
    if (!api || proposing) return;
    setProposing(kind);
    try {
      const next =
        kind === 'suggested'
          ? (await api.suggestFilingPlan(job.id)).plan
          : await api.getStarterFilingPlan(job.id);
      if (live.current) setProposal({ kind, plan: next });
    } catch (reason) {
      if (live.current)
        setNotice(
          reason instanceof Error ? reason.message : 'A naming suggestion could not be made.',
        );
    } finally {
      if (live.current) setProposing(null);
    }
  };
  const acceptProposal = () => {
    if (!proposal) return;
    setRules(proposal.plan.rules.map(cloneRule));
    setProposal(null);
    setSelection(null);
    setNotice(
      proposal.kind === 'suggested'
        ? 'Every document type now uses the suggested naming.'
        : 'Every document type now uses the basic naming.',
    );
  };

  const copyTargets = types.filter((item) => item.id !== type?.id);
  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  const applyCopy = () => {
    if (!rule || !type) return;
    const adapt = (expression: FilingExpression, target: NamingDocumentType): FilingExpression => ({
      separator: expression.separator,
      parts: expression.parts.flatMap((part): FilingToken[] => {
        if (part.kind !== 'field') return [{ ...part }];
        const source = type.fields.find((field) => field.id === part.fieldId);
        const match =
          source &&
          target.fields.find(
            (field) =>
              field.kind !== 'line-items' &&
              field.name.localeCompare(source.name, undefined, { sensitivity: 'accent' }) === 0,
          );
        return match
          ? [
              {
                ...part,
                fieldId: match.id,
                fallback: `Unknown ${match.name}`,
                format: match.kind === 'date' ? (part.format ?? 'yyyy-MM-dd') : null,
              },
            ]
          : [];
      }),
    });
    const copied = copyTargets.filter((target) => copyPicks.has(target.id)).length;
    setRules((current) =>
      current.map((item) => {
        const target = copyTargets.find((candidate) => candidate.id === item.documentTypeId);
        if (!target || !copyPicks.has(target.id)) return item;
        const folders = rule.folderSegments
          .map((expression) => adapt(expression, target))
          .filter((expression) => expression.parts.length);
        const name = adapt(rule.preferredBaseName, target);
        const fallbackName =
          starter?.rules.find((candidate) => candidate.documentTypeId === target.id)
            ?.preferredBaseName ??
          ({ parts: [{ kind: 'document-type' }], separator: '_' } as FilingExpression);
        return {
          documentTypeId: target.id,
          folderSegments: folders,
          preferredBaseName: name.parts.length
            ? name
            : cloneRule({ documentTypeId: '', folderSegments: [], preferredBaseName: fallbackName })
                .preferredBaseName,
        };
      }),
    );
    setCopyOpen(false);
    setCopyPicks(new Set());
    setNotice(
      copied
        ? `Copied to ${copied} ${copied === 1 ? 'type' : 'types'}. Fields a type doesn’t have were left out.`
        : '',
    );
  };

  const use = async () => {
    if (saving) return;
    setSaving(true);
    setSaveError('');
    try {
      await onUse({ ...plan, version: plan.version + 1, rules });
    } catch (reason) {
      if (!live.current) return;
      setAskClose(false);
      setSaveError(reason instanceof Error ? reason.message : 'The naming could not be saved.');
    } finally {
      if (live.current) setSaving(false);
    }
  };
  const requestClose = () => {
    if (dirty) setAskClose(true);
    else onClose();
  };
  const shownTypes =
    types.length > 6
      ? types.filter((item) => item.name.toLowerCase().includes(query.trim().toLowerCase()))
      : types;

  // The same place and words as Job setup's own save group.
  const actions = dirty ? (
    <div className="setup-save-group" role="group" aria-label="Unsaved changes">
      <span className="setup-save-state">
        <span className="setup-save-dot" aria-hidden="true" />
        Unsaved changes
      </span>
      <button type="button" className="quiet-button small" onClick={() => setAskDiscard(true)}>
        Discard
      </button>
      <button
        type="button"
        className="primary-button small"
        disabled={!canUse || saving}
        aria-busy={saving}
        title={errors.length ? errors[0].message : undefined}
        data-telemetry="naming-use"
        onClick={() => void use()}
      >
        {saving ? <LoaderCircle className="spin" size={14} /> : <Check size={14} />} Use this naming
      </button>
    </div>
  ) : undefined;

  return (
    <WorkspaceDialog
      telemetry="naming"
      title="File naming"
      onClose={requestClose}
      actions={actions}
    >
      <div className="naming-window">
        <aside className="naming-types" aria-label="Document types">
          {types.length > 6 && (
            <>
              <label className="naming-search">
                <Search size={15} aria-hidden="true" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Find a document type"
                  aria-label="Find a document type"
                />
              </label>
              <small className="naming-count">{types.length} document types</small>
            </>
          )}
          <div className="naming-type-list" role="listbox" aria-label="Document types">
            {shownTypes.map((item) => {
              const itemRule = rules.find((candidate) => candidate.documentTypeId === item.id);
              const changed =
                Boolean(starter) &&
                !sameRule(
                  itemRule,
                  starter?.rules.find((candidate) => candidate.documentTypeId === item.id),
                );
              return (
                <button
                  key={item.id}
                  type="button"
                  role="option"
                  aria-selected={item.id === type?.id}
                  className={`naming-type${item.id === type?.id ? ' selected' : ''}`}
                  onClick={() => {
                    setTypeId(item.id);
                    setSelection(null);
                    setAdding(null);
                    setSwapping(false);
                    setCopyOpen(false);
                  }}
                >
                  <span className="naming-type-name">
                    {item.name}
                    {changed && (
                      <i className="naming-changed-dot" aria-label="Differs from the suggestion" />
                    )}
                  </span>
                  <small>{namingSummary(itemRule, item)}</small>
                </button>
              );
            })}
            {shownTypes.length === 0 && (
              <small className="naming-empty">No document type matches “{query}”.</small>
            )}
          </div>
          {type && copyTargets.length > 0 && (
            <div className="naming-copy">
              <button
                type="button"
                className="secondary-button small"
                aria-expanded={copyOpen}
                onClick={() => {
                  setCopyOpen((value) => !value);
                  setCopyPicks(new Set());
                }}
              >
                <Copy size={14} /> Copy {type.name} naming to…
              </button>
              {copyOpen && (
                <div
                  className="naming-popover naming-copy-popover"
                  role="menu"
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') {
                      event.stopPropagation();
                      setCopyOpen(false);
                    }
                  }}
                >
                  <strong>Give these types the same folders and file name</strong>
                  <div className="naming-copy-list">
                    {copyTargets.map((target) => (
                      <label key={target.id} className="naming-check">
                        <input
                          type="checkbox"
                          checked={copyPicks.has(target.id)}
                          onChange={() =>
                            setCopyPicks((current) => {
                              const next = new Set(current);
                              if (next.has(target.id)) next.delete(target.id);
                              else next.add(target.id);
                              return next;
                            })
                          }
                        />
                        {target.name}
                      </label>
                    ))}
                  </div>
                  <div className="naming-copy-actions">
                    <button
                      type="button"
                      className="quiet-button small"
                      onClick={() => setCopyPicks(new Set(copyTargets.map((target) => target.id)))}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="primary-button small"
                      disabled={copyPicks.size === 0}
                      onClick={applyCopy}
                    >
                      {copyPicks.size
                        ? `Copy to ${copyPicks.size} ${copyPicks.size === 1 ? 'type' : 'types'}`
                        : 'Copy'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </aside>

        <section className="naming-center" aria-label={type ? `${type.name} naming` : 'Naming'}>
          {type && rule ? (
            <>
              <header className="naming-center-head">
                <h3>{type.name} naming</h3>
                {starterRule && !sameRule(rule, starterRule) && (
                  <button
                    type="button"
                    className="quiet-button small"
                    onClick={() =>
                      editRule(
                        (draft) => {
                          draft.folderSegments = cloneRule(starterRule).folderSegments;
                          draft.preferredBaseName = cloneRule(starterRule).preferredBaseName;
                        },
                        { selection: null },
                      )
                    }
                  >
                    <Undo2 size={14} /> Back to the suggestion
                  </button>
                )}
              </header>
              {proposal && (
                <div
                  className="naming-proposal"
                  role="region"
                  aria-label={proposal.kind === 'suggested' ? 'Suggested naming' : 'Basic naming'}
                >
                  <Sparkles size={16} aria-hidden="true" />
                  <span>
                    <strong>
                      {proposal.kind === 'suggested'
                        ? 'Suggested naming is ready'
                        : 'Basic naming is ready'}
                    </strong>
                    <small>
                      It replaces the naming of every document type. Nothing changes until you use
                      it.
                    </small>
                  </span>
                  <button
                    type="button"
                    className="quiet-button small"
                    onClick={() => setProposal(null)}
                  >
                    Keep current naming
                  </button>
                  <button type="button" className="primary-button small" onClick={acceptProposal}>
                    <Check size={13} /> Use it
                  </button>
                </div>
              )}
              <div className="naming-lanes">
                {lanes.map((lane) => {
                  const expression = expressionOf(rule, lane);
                  const isName = lane === 'name';
                  // Each part's value on the latest document, when the example splits cleanly into the parts.
                  const sampleText = !firstExample
                    ? ''
                    : isName
                      ? firstExample.preferredBaseName
                      : (firstExample.folders[lane as number] ?? '');
                  const pieces =
                    expression.separator && sampleText
                      ? sampleText.split(expression.separator)
                      : sampleText && expression.parts.length === 1
                        ? [sampleText]
                        : [];
                  const partSamples = pieces.length === expression.parts.length ? pieces : null;
                  return (
                    <div key={String(lane)} className="naming-lane-wrap">
                      {isName && (
                        <button
                          type="button"
                          className="naming-add-level"
                          onClick={() =>
                            editRule(
                              (draft) => {
                                const date = nameableFields(type).find(
                                  (field) => field.kind === 'date',
                                );
                                draft.folderSegments.push({
                                  parts: [
                                    date ? fieldToken(date, 'yyyy') : { kind: 'document-type' },
                                  ],
                                  separator: '_',
                                });
                              },
                              { selection: { lane: rule.folderSegments.length, index: 0 } },
                            )
                          }
                        >
                          <Plus size={15} /> Add a folder level
                        </button>
                      )}
                      <div className={`naming-lane${isName ? ' file' : ''}`}>
                        <div className="naming-lane-label">
                          {isName ? (
                            <FileText size={17} aria-hidden="true" />
                          ) : (
                            <FolderOpen size={17} aria-hidden="true" />
                          )}
                          {isName ? 'File name' : `Folder ${lane + 1}`}
                        </div>
                        <div className="naming-lane-body">
                          <div className="naming-parts">
                            {expression.parts.map((part, index) => {
                              const field =
                                part.kind === 'field'
                                  ? type.fields.find((item) => item.id === part.fieldId)
                                  : undefined;
                              const selected =
                                selection?.lane === lane && selection.index === index;
                              return (
                                <span key={index} className="naming-part-slot">
                                  {index > 0 && expression.separator !== '' && (
                                    <span
                                      className="naming-separator"
                                      aria-label={`then ${SEPARATORS.find((item) => item.value === expression.separator)?.name ?? 'separator'}`}
                                    >
                                      {expression.separator === ' ' ? '␣' : expression.separator}
                                    </span>
                                  )}
                                  <button
                                    type="button"
                                    className={`naming-part ${part.kind}${selected ? ' selected' : ''}${field && !visibleFilingField(field) ? ' private' : ''}`}
                                    aria-pressed={selected}
                                    onClick={() => {
                                      setSelection({ lane, index });
                                      setAdding(null);
                                      setSwapping(false);
                                    }}
                                  >
                                    <small>{partLabel(part, type)}</small>
                                    <b>{partSamples?.[index] || partValue(part, type)}</b>
                                  </button>
                                </span>
                              );
                            })}
                            <span className="naming-anchor">
                              <TooltipButton
                                type="button"
                                className={`naming-add-part${adding === lane ? ' open' : ''}`}
                                aria-label={`Add a part to ${isName ? 'the file name' : `folder ${lane + 1}`}`}
                                aria-expanded={adding === lane}
                                tooltip="Add a part"
                                onClick={() => {
                                  setAdding(adding === lane ? null : lane);
                                  setSwapping(false);
                                }}
                              >
                                <Plus size={16} />
                              </TooltipButton>
                              {adding === lane && (
                                <PartMenu
                                  choices={partChoices((token) => {
                                    editRule(
                                      (draft) => {
                                        expressionOf(draft, lane).parts.push(token);
                                      },
                                      { selection: { lane, index: expression.parts.length } },
                                    );
                                    setAdding(null);
                                  })}
                                  onClose={() => setAdding(null)}
                                />
                              )}
                            </span>
                            {isName && <span className="naming-extension">.pdf</span>}
                            {expression.parts.length > 1 && (
                              <select
                                className="naming-separator-choice"
                                value={expression.separator}
                                title="Between parts"
                                aria-label={`Between parts in ${isName ? 'the file name' : `folder ${lane + 1}`}`}
                                onChange={(event) => {
                                  const value = event.target.value;
                                  editRule((draft) => {
                                    expressionOf(draft, lane).separator = value;
                                  });
                                }}
                              >
                                {SEPARATORS.map((item) => (
                                  <option key={item.name} value={item.value}>
                                    {item.value === ''
                                      ? 'No gap'
                                      : item.value === ' '
                                        ? 'Space'
                                        : `${item.label}  ${item.name.toLowerCase()}`}
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>
                        </div>
                        {!isName ? (
                          <button
                            type="button"
                            className="icon-button naming-lane-remove"
                            aria-label={`Remove folder level ${lane + 1}`}
                            title="Remove folder level"
                            onClick={() =>
                              editRule(
                                (draft) => {
                                  draft.folderSegments.splice(lane, 1);
                                },
                                { selection: null },
                              )
                            }
                          >
                            <X size={16} />
                          </button>
                        ) : (
                          <span />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {selectedPart && selection && selectedExpression && (
                <div className="naming-inspector" aria-live="polite">
                  <>
                    <div className="naming-inspector-head">
                      <strong>{partLabel(selectedPart, type)}</strong>
                      <span>
                        in{' '}
                        {selection.lane === 'name'
                          ? 'the file name'
                          : `folder ${selection.lane + 1}`}
                      </span>
                      <div className="naming-inspector-actions">
                        <TooltipButton
                          type="button"
                          className="secondary-button small"
                          aria-label="Move left"
                          tooltip="Move left"
                          disabled={selection.index === 0}
                          onClick={() =>
                            editRule(
                              (draft) => {
                                const parts = expressionOf(draft, selection.lane).parts;
                                [parts[selection.index - 1], parts[selection.index]] = [
                                  parts[selection.index],
                                  parts[selection.index - 1],
                                ];
                              },
                              { selection: { ...selection, index: selection.index - 1 } },
                            )
                          }
                        >
                          <ArrowLeft size={15} />
                        </TooltipButton>
                        <TooltipButton
                          type="button"
                          className="secondary-button small"
                          aria-label="Move right"
                          tooltip="Move right"
                          disabled={selection.index >= selectedExpression.parts.length - 1}
                          onClick={() =>
                            editRule(
                              (draft) => {
                                const parts = expressionOf(draft, selection.lane).parts;
                                [parts[selection.index + 1], parts[selection.index]] = [
                                  parts[selection.index],
                                  parts[selection.index + 1],
                                ];
                              },
                              { selection: { ...selection, index: selection.index + 1 } },
                            )
                          }
                        >
                          <ArrowRight size={15} />
                        </TooltipButton>
                        <button
                          type="button"
                          className="secondary-button small"
                          disabled={
                            selection.lane === 'name' && selectedExpression.parts.length === 1
                          }
                          title={
                            selection.lane === 'name' && selectedExpression.parts.length === 1
                              ? 'A file name needs at least one part'
                              : undefined
                          }
                          onClick={() =>
                            editRule(
                              (draft) => {
                                const expression = expressionOf(draft, selection.lane);
                                expression.parts.splice(selection.index, 1);
                                if (selection.lane !== 'name' && expression.parts.length === 0)
                                  draft.folderSegments.splice(selection.lane, 1);
                              },
                              { selection: null },
                            )
                          }
                        >
                          <X size={14} /> Remove
                        </button>
                      </div>
                    </div>
                    <InspectorRow label="Take the value from">
                      <span className="naming-anchor">
                        <button
                          type="button"
                          className="naming-dropdown"
                          aria-expanded={swapping}
                          onClick={() => {
                            setSwapping((value) => !value);
                            setAdding(null);
                          }}
                        >
                          {partLabel(selectedPart, type)}
                          <ChevronDown size={14} />
                        </button>
                        {swapping && (
                          <PartMenu
                            choices={partChoices((token) => {
                              setPart(selection.lane, selection.index, token);
                              setSwapping(false);
                            })}
                            onClose={() => setSwapping(false)}
                          />
                        )}
                      </span>
                    </InspectorRow>
                    {selectedField?.kind === 'date' && (
                      <InspectorRow label="Show as">
                        <div
                          className="naming-seg naming-formats"
                          role="group"
                          aria-label="Date format"
                        >
                          {DATE_FORMATS.map((format) => (
                            <button
                              key={format.value}
                              type="button"
                              aria-pressed={selectedPart.format === format.value}
                              className={selectedPart.format === format.value ? 'active' : ''}
                              onClick={() =>
                                setPart(selection.lane, selection.index, {
                                  ...selectedPart,
                                  format: format.value,
                                })
                              }
                            >
                              <span>{format.label}</span>
                              <small>{format.example}</small>
                            </button>
                          ))}
                        </div>
                      </InspectorRow>
                    )}
                    {selectedPart.kind === 'field' && (
                      <InspectorRow label="If it’s missing, use">
                        <input
                          className="naming-text"
                          value={selectedPart.fallback ?? ''}
                          aria-label="Wording when the value is missing"
                          onChange={(event) =>
                            setPart(selection.lane, selection.index, {
                              ...selectedPart,
                              fallback: event.target.value,
                            })
                          }
                        />
                      </InspectorRow>
                    )}
                    {selectedPart.kind === 'literal' && (
                      <InspectorRow label="Your words">
                        <input
                          className="naming-text"
                          value={selectedPart.literal ?? ''}
                          aria-label="Your own words"
                          onChange={(event) =>
                            setPart(selection.lane, selection.index, {
                              ...selectedPart,
                              literal: event.target.value,
                            })
                          }
                        />
                      </InspectorRow>
                    )}
                    {selectedField && !visibleFilingField(selectedField) && (
                      <p className="naming-private-note">
                        <ShieldAlert size={16} aria-hidden="true" />
                        {selectedField.name} is private information. Anyone who can open{' '}
                        {destinationLabel} will see it in names. You can keep it.
                      </p>
                    )}
                  </>
                </div>
              )}
              {saveError && (
                <div className="naming-errors" role="alert">
                  <p>
                    <TriangleAlert size={14} aria-hidden="true" />
                    {saveError}
                  </p>
                </div>
              )}
              {errors.length > 0 && (
                <div className="naming-errors" role="alert" data-telemetry="error:naming-invalid">
                  {errors.slice(0, 3).map((item) => (
                    <p key={`${item.code}:${item.message}`}>
                      <TriangleAlert size={14} aria-hidden="true" />
                      {item.message}
                    </p>
                  ))}
                </div>
              )}
              <footer className="naming-suggest">
                <span className="naming-anchor">
                  <TooltipButton
                    type="button"
                    className="icon-button naming-more"
                    aria-label="More naming choices"
                    aria-expanded={moreOpen}
                    disabled={proposing !== null}
                    tooltip="More"
                    onClick={() => setMoreOpen((value) => !value)}
                  >
                    {proposing ? (
                      <LoaderCircle className="spin" size={16} />
                    ) : (
                      <MoreHorizontal size={18} />
                    )}
                  </TooltipButton>
                  {moreOpen && (
                    <PartMenu
                      choices={[
                        {
                          key: 'suggested',
                          label: 'Suggest naming',
                          hint: '',
                          private: false,
                          pick: () => {
                            setMoreOpen(false);
                            void proposeFrom('suggested');
                          },
                        },
                        {
                          key: 'starter',
                          label: 'Start from the basic naming',
                          hint: '',
                          private: false,
                          pick: () => {
                            setMoreOpen(false);
                            void proposeFrom('starter');
                          },
                        },
                      ]}
                      onClose={() => setMoreOpen(false)}
                    />
                  )}
                </span>
                {notice && (
                  <small className="naming-notice" role="status">
                    {notice}
                  </small>
                )}
              </footer>
            </>
          ) : (
            <p className="naming-inspector-empty">This job has no document types to name yet.</p>
          )}
        </section>

        <aside className="naming-preview" aria-label="Examples">
          <header>
            <FolderOpen size={16} aria-hidden="true" />
            <strong>{destinationLabel}</strong>
            <small>your latest {type ? type.name.toLowerCase() : 'documents'}</small>
          </header>
          <div className="naming-tree">
            {previewState === 'loading' && !preview && (
              <p className="naming-tree-state" role="status">
                <LoaderCircle className="spin" size={14} /> Building examples…
              </p>
            )}
            {previewState === 'error' && (
              <div
                className="naming-tree-state attention"
                role="alert"
                data-telemetry="error:filing-examples-failed"
              >
                <TriangleAlert size={14} />
                <span>{previewError}</span>
                <button
                  type="button"
                  className="quiet-button small"
                  onClick={() => setPreviewRetry((value) => value + 1)}
                >
                  Try again
                </button>
              </div>
            )}
            {previewState !== 'error' && preview && tree.length === 0 && (
              <p className="naming-tree-state">
                No current {type?.name.toLowerCase() ?? 'document'} to show yet. Names appear here
                once one is scanned.
              </p>
            )}
            {tree.map((row) => (
              <div
                key={row.key}
                className={`naming-tree-row ${row.kind}${row.kind === 'file' && firstExample && row.key.endsWith(`\u0001${firstExample.documentId}`) ? ' current' : ''}`}
                style={{ paddingLeft: `${row.depth * 18 + 6}px` }}
                title={row.text}
              >
                {row.kind === 'folder' ? (
                  <FolderOpen size={15} aria-hidden="true" />
                ) : (
                  <FileText size={15} aria-hidden="true" />
                )}
                <span>{row.text}</span>
                {row.fallback && (
                  <Info
                    className="naming-fallback-mark"
                    size={13}
                    aria-label="Used the missing-value wording"
                  />
                )}
              </div>
            ))}
          </div>
          {preview && (
            <div className="naming-facts">
              <p className="ok">
                <Check size={15} aria-hidden="true" />
                {preview.valid.toLocaleString()} of {preview.documents.toLocaleString()} current
                documents can be named
              </p>
              {fallbackCount > 0 && (
                <p className="info">
                  <Info size={15} aria-hidden="true" />
                  {fallbackCount} {fallbackCount === 1 ? 'example used' : 'examples used'} the
                  missing-value wording
                </p>
              )}
              {preview.exactCollisions > 0 ? (
                <p className="info">
                  <Info size={15} aria-hidden="true" />
                  {preview.exactCollisions}{' '}
                  {preview.exactCollisions === 1 ? 'name is' : 'names are'} shared. The second file
                  gets “-2” at the end, so nothing is overwritten.
                </p>
              ) : (
                <p className="ok">
                  <Check size={15} aria-hidden="true" />
                  No two files get the same name
                </p>
              )}
              {privateFields.length > 0 && (
                <p className="warn">
                  <ShieldAlert size={15} aria-hidden="true" />
                  {privateFields.join(', ')} {privateFields.length === 1 ? 'is' : 'are'} private and{' '}
                  {privateFields.length === 1 ? 'shows' : 'show'} in names.
                </p>
              )}
            </div>
          )}
        </aside>
      </div>

      {askDiscard && (
        <WorkspaceDialog
          telemetry="naming-discard"
          title="Discard your naming changes?"
          compact
          onClose={() => setAskDiscard(false)}
        >
          <div className="naming-prompt">
            <p>The naming goes back to how it was when you opened this window.</p>
            <div className="naming-prompt-actions">
              <button
                type="button"
                className="secondary-button"
                onClick={() => {
                  setRules(
                    plan.rules
                      .map(cloneRule)
                      .concat(
                        (starter?.rules ?? [])
                          .filter(
                            (item) =>
                              !plan.rules.some(
                                (saved) => saved.documentTypeId === item.documentTypeId,
                              ),
                          )
                          .map(cloneRule),
                      ),
                  );
                  setSelection(null);
                  setProposal(null);
                  setAskDiscard(false);
                }}
              >
                Discard changes
              </button>
              <button type="button" className="quiet-button" onClick={() => setAskDiscard(false)}>
                Keep editing
              </button>
            </div>
          </div>
        </WorkspaceDialog>
      )}
      {askClose && (
        <WorkspaceDialog
          telemetry="naming-close"
          title="Use this naming?"
          compact
          onClose={() => setAskClose(false)}
        >
          <div className="naming-prompt">
            <p>
              {savesNow
                ? 'You changed how files are named. Using it saves it for this job now; every destination names new documents this way.'
                : 'You changed how files are named. Using it makes it part of Job setup’s unsaved changes.'}
            </p>
            <div className="naming-prompt-actions">
              <button
                type="button"
                className="primary-button"
                data-telemetry="naming-use-on-close"
                disabled={!canUse || saving}
                onClick={() => void use()}
              >
                Use this naming
              </button>
              <button type="button" className="secondary-button" onClick={onClose}>
                Close without using
              </button>
              <button type="button" className="quiet-button" onClick={() => setAskClose(false)}>
                Keep editing
              </button>
            </div>
          </div>
        </WorkspaceDialog>
      )}
    </WorkspaceDialog>
  );
}

function InspectorRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="naming-inspector-row">
      <span className="naming-inspector-label">{label}</span>
      <div className="naming-inspector-value">{children}</div>
    </div>
  );
}

function PartMenu({
  choices,
  onClose,
}: {
  choices: Array<{ key: string; label: string; hint: string; private: boolean; pick: () => void }>;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // The callers pass a new onClose on every render. Read through a ref, so the menu takes focus
  // once as it opens: re-running on each render (the naming preview landing) moved focus back
  // to the first item, and Enter added that part instead of the one arrowed to.
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const away = (event: MouseEvent) => {
      if (!ref.current?.parentElement?.contains(event.target as Node)) close.current();
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);
  return (
    <div
      ref={ref}
      className="naming-popover naming-menu"
      role="menu"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          event.preventDefault();
          onClose();
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          const items = [...(ref.current?.querySelectorAll<HTMLButtonElement>('button') ?? [])];
          const at = items.indexOf(document.activeElement as HTMLButtonElement);
          items[(at + (event.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length]?.focus();
        }
      }}
    >
      {choices.map((choice) => (
        <button key={choice.key} type="button" role="menuitem" onClick={choice.pick}>
          {choice.private && (
            <ShieldAlert className="naming-private-icon" size={14} aria-label="Private" />
          )}
          <span>{choice.label}</span>
          {choice.hint && <small>{choice.hint}</small>}
        </button>
      ))}
    </div>
  );
}
